import { useEffect, useState } from "react";
import { base44 } from "@/api/base44Client";

// Cache a nivel módulo: la lista de branding se consulta desde Layout, LandingNav
// y LandingFooter simultáneamente. Un solo fetch por carga. Además, una suscripción
// realtime a DomainBranding mantiene el cache y todos los hooks sincronizados:
// cuando el admin actualiza el logo (o cualquier dato de branding) desde la
// configuración de franquicia/SaaS, el cambio se propaga en vivo a todas las
// partes donde se invoca el logo (sidebar, header, footer, landing) sin recargar.
let cache = null; // null = no cargado, [] = cargado vacío
let inflight = null;
let bound = false; // la suscripción realtime es global (una sola vez por sesión)
const listeners = new Set();

async function fetchBrandings() {
  return base44.entities.DomainBranding
    .list("-created_date", 100)
    .catch(() => [])
    .then((list) => { cache = list || []; return cache; });
}

async function loadBrandings() {
  if (cache) return cache;
  if (!inflight) inflight = fetchBrandings().finally(() => { inflight = null; });
  return inflight;
}

// Recarga forzada (tras un cambio en la config o un evento realtime) y avisa a
// todos los hooks activos para que re-resuelvan el branding de su hostname.
async function refreshBrandings() {
  inflight = fetchBrandings().finally(() => { inflight = null; });
  cache = await inflight;
  listeners.forEach((fn) => fn());
  return cache;
}

function ensureSubscription() {
  if (bound) return;
  bound = true;
  // La suscripción realtime abre un WebSocket que falla y reintententa en la
  // landing pública (sin sesión), generando errores de consola. Solo se
  // suscribe cuando hay un usuario autenticado: el live-update de branding
  // interesa al admin, no al visitante anónimo.
  base44.auth.isAuthenticated().then((authed) => {
    if (!authed) { bound = false; return; }
    try {
      base44.entities.DomainBranding.subscribe(() => { refreshBrandings(); });
    } catch { /* subscribe opcional: si falla, el cache sigue sirviendo */ }
  }).catch(() => { bound = false; });
}

export function matchBranding(list, hostname) {
  if (!list || !hostname) return null;
  const active = list.filter((b) => b.active !== false);
  // 1) coincidencia exacta
  let match = active.find((b) => b.domain && b.domain.toLowerCase() === hostname.toLowerCase());
  if (match) return match;
  // 2) subdominio (ej. app.mi.marca.com coincide con mi.marca.com)
  match = active
    .filter((b) => b.domain && hostname.toLowerCase().endsWith("." + b.domain.toLowerCase()))
    .sort((a, b) => (b.domain?.length || 0) - (a.domain?.length || 0))[0];
  if (match) return match;
  // 3) dominio marcado como principal por defecto
  return active.find((b) => b.is_default) || null;
}

export function useDomainBranding() {
  const [branding, setBranding] = useState(null);
  useEffect(() => {
    let active = true;
    const hostname = typeof window !== "undefined" ? window.location.hostname : "";
    const resolve = () => { if (active) setBranding(matchBranding(cache, hostname)); };
    loadBrandings().then(resolve);
    ensureSubscription();
    listeners.add(resolve);
    return () => { active = false; listeners.delete(resolve); };
  }, []);
  return branding;
}

// Invalida el caché tras guardar/editar desde el panel admin y fuerza la
// notificación a todos los hooks montados (Layout, LandingNav, LandingFooter…)
// para que el logo nuevo se pinte de inmediato en toda la app.
export function invalidateDomainBrandingCache() {
  refreshBrandings();
}